import { HelperMessage, Label } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { LayoutSettingSelectedEventData } from '@databases/analytics/src/types';
import FieldPicker from '@databases/field-picker/FieldPicker';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseField, DatabaseFieldType } from '@databases/types/database';
import {
  DatabaseCoverFieldType,
  DatabaseViewSettings,
} from '@databases/types/database/view';
import { getCoverField, getDatabaseFields } from '@databases/utils/database/utils';
import { ViewChangeType, ViewChangeTypes } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../i18n';

export interface CoverFieldSettingProps {
  contentId: string;
  value: DatabaseViewSettings['layout'];
  onChange: (value: ViewChangeType) => void;
  source: LayoutSettingSelectedEventData['source'];
}

const CoverImageSetting: FunctionComponent<CoverFieldSettingProps> = ({
  contentId,
  value,
  onChange,
  source,
}) => {
  const { formatMessage } = useIntl();
  const client = useAnalytics();

  const [database, possibleFields] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });

    return [
      database,
      [
        ...getDatabaseFields(database).filter((field) =>
          [
            DatabaseFieldType.ATTACHMENT,
            DatabaseFieldType.PAGE_LINK,
            DatabaseFieldType.ENTRY_LINK,
          ].includes(field.type)
        ),
      ],
    ] as const;
  });

  const resolvedValue = useMemo(
    () => getCoverField(database, value.coverFieldId),
    [database, value.coverFieldId]
  );

  const handleChange = useCallback(
    (field: DatabaseField | undefined) => {
      onChange({
        type: ViewChangeTypes.LAYOUT_COVER_FIELD_ID,
        value: field ? field.id : DatabaseCoverFieldType.NONE,
      });
      client.sendLayoutSettingSelectedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source,
        layoutSetting: 'coverImage',
        value: field ? field.id : DatabaseCoverFieldType.NONE,
      });
    },
    [client, contentId, onChange, source]
  );

  return (
    <Box>
      <Label htmlFor="cover-field-setting">
        {formatMessage(messages['view-settings.layout.cover-image.label'])}
      </Label>
      <FieldPicker
        fields={possibleFields}
        value={resolvedValue}
        onChange={handleChange}
        isClearable
      />
      <HelperMessage>
        {formatMessage(messages['view-settings.layout.cover-image.helper-message'])}
      </HelperMessage>
    </Box>
  );
};

export default CoverImageSetting;
